import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { sendQuoteToClient, notifyClientOfQuoteUpdate } from '../services/quoteService';
import { apiClient, freshQuoteNumber } from '@/lib/api';
import { formatDateToISO, formatDateToISOWithTimezone } from '@/utils';
import { ROUTES } from '../../../constants';
import { getAirportTimezone } from '@utils/airportTimezone';
import { toast } from "sonner";
import { trunc2 } from '@/utils';
/**
 * Build leg itinerary array from flights
 */
const buildLegItinerary = (flights) =>
  (flights || []).map((flight) => ({
    pax: flight.passengerCount || 1,
    departure_date: formatDateToISOWithTimezone(flight.departureDate, flight.departureTime, getAirportTimezone(flight.departure?.icao)) || null,
    arrival_address: null,
    arrival_airport: flight.arrival?.icao || "",
    departure_address: null,
    departure_airport: flight.departure?.icao || "",
    notes: flight.notes || "",
  }));

/**
 * Extract client name from quote data
 */
const getClientName = (clientData) =>
  [clientData?.firstName || clientData?.first_name, clientData?.lastName || clientData?.last_name]
    .filter(Boolean).join(' ') || "";

/**
 * Build bid payload from quote data
 */
const buildBidPayload = (quoteData, { finalPrice, createdBy, brokerOrgId }) => ({
  trip_number: `T${Math.floor(Math.random() * 1000000)}`,
  bid_number: `Q${Date.now()}`,
  created_by: createdBy,
  broker_id: createdBy,
  broker_org_id: brokerOrgId,
  client_name: getClientName(quoteData.client?.clientData),
  client_email: quoteData.client?.clientData?.email || "",
  client_phone: quoteData.client?.clientData?.phone || "",
  departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
  arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
  departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
  baggage_requirements: quoteData.flights?.[0]?.notes || "",
  special_requests: quoteData.settings?.notes || "",
  status: "DRAFT",
  total_amount: trunc2(parseFloat(finalPrice) || 0),
  currency: "USD",
  valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
  sent_at: null,
  viewed_at: null,
  accepted_at: null,
  notes: quoteData.settings?.notes || "",
  leg_itinerary: buildLegItinerary(quoteData.flights),
});

/**
 * Build quote payload with pricing fields
 */
const buildQuotePayload = (quoteData, { brokerOrgId, createdBy, bidItems, status, settings }) => {
  const airportZone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  return {
    ...quoteData,
    broker_org_id: brokerOrgId,
    created_by: createdBy,
    bid_items: bidItems,
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, airportZone) || null,
    return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, airportZone) || (quoteData.flightType === "round-trip" ? formatDateToISOWithTimezone(quoteData.flights?.[1]?.departureDate, quoteData.flights?.[1]?.departureTime, airportZone) : null),
    total_due: settings?.total_cost || 0,
    broker_margin: settings?.broker_margin || 0,
    platform_fee: settings?.platform_fee || 0,
    federal_excise_tax: settings?.federal_excise_tax || 0,
    segment_tax: settings?.segment_tax || 0,
    operator_cost: settings?.operator_cost || 0,
    taxable_amount: settings?.taxable_amount || 0,
    card_fee: settings?.card_fee || 0,
    status
  };
};

/**
 * Build a CLEAN quote payload for POST /quotes (save-as-draft path).
 *
 * Unlike `buildQuotePayload` (which spreads the whole form state and is only
 * safe because `sendQuoteToClient` re-maps it before POSTing), this returns the
 * exact whitelist the API accepts. Spreading `...quoteData` here sends form-only
 * structures (client object, Date objects in `flights`, a `contractFile` File in
 * `settings`, `_editing`, etc.) which the backend rejects with HTTP 500.
 */
const buildQuoteCreatePayload = (quoteData, { brokerOrgId, createdBy, bidItems, status, settings }) => {
  const airportZone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  return {
    client_id: quoteData.client?.clientData?.client_id || quoteData.client?.clientData?.id || null,
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, airportZone) || null,
    return_date: resolveReturnDate(quoteData),
    passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
    quote_number: settings?.quoteNumber || freshQuoteNumber(),
    departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
    arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
    is_multi_leg: quoteData.flightType === "multi-leg",
    is_round_trip: quoteData.flightType === "round-trip",
    broker_org_id: brokerOrgId,
    created_by: createdBy,
    status,
    bid_items: bidItems,
    total_due: settings?.final_price || 0,
    broker_margin: settings?.broker_margin || 0,
    platform_fee: settings?.platform_fee || 0,
    note_to_client: settings?.notes || "",
    federal_excise_tax: settings?.federal_excise_tax || 0,
    operator_cost: settings?.operator_cost || 0,
    taxable_amount: settings?.taxable_amount || 0,
    segment_tax: settings?.segment_tax || 0,
  };
};

/**
 * Build bid update payload for existing trip group bids
 */
const buildBidUpdatePayload = (quoteData, finalPrice) => ({
  client_name: getClientName(quoteData.client?.clientData),
  client_email: quoteData.client?.clientData?.email || "",
  client_phone: quoteData.client?.clientData?.phone || "",
  departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
  arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
  departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
  baggage_requirements: quoteData.flights?.[0]?.notes || "",
  special_requests: quoteData.settings?.notes || "",
  status: "SENT",
  total_amount: trunc2(finalPrice || 0),
  currency: "USD",
  valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
  sent_at: null,
  viewed_at: null,
  accepted_at: null,
  notes: quoteData.settings?.notes || "",
  leg_itinerary: buildLegItinerary(quoteData.flights),
});

/**
 * Create bid options for a given bid
 */
const createBidOptions = async (bidId, bidOptions) => {
  if (!bidOptions || bidOptions.length === 0) return;
  const promises = bidOptions.map((option, index) => {
    const payload = {
      bid_id: bidId,
      aircraft_id: option?.aircraft_id || "",
      operator_id: option?.operator_id || "",
      base_price: "0.00",
      fuel_cost: "0.00",
      landing_fees: "0.00",
      crew_fees: "0.00",
      catering_cost: "0.00",
      ground_transport: "0.00",
      other_fees: "0.00",
      total_price: trunc2(option?.total_price || 0),
      broker_markup: trunc2(option?.broker_markup || 0),
      final_price: trunc2(option?.final_price || 0),
      federal_excise_tax: trunc2(option?.federal_excise_tax || 0),
      segment_tax: trunc2(option?.segment_tax || 0),
      platform_fee: trunc2(option?.platform_fee || 0),
      operator_cost: trunc2(option?.operator_cost || 0),
      is_recommended: index === 0,
      recommendation_reason: index === 0 ? "." : "",
      availability_confirmed: true,
      shortlisted: true,
      risk_level: "LOW",
      trust_score: "0.00",
      estimated_flight_time: 0,
      aircraft_features: { beds: 1, wifi: false, entertainment: false }
    };
    return apiClient.createBidOption(payload);
  });
  await Promise.all(promises);
};

/**
 * Update existing bid options via API
 */
const updateExistingBidOptions = async (bidOptions) => {
  if (!bidOptions || bidOptions.length === 0) return;
  const promises = bidOptions.map(async (option) => {
    const bidOptionId = option.bid_option_id;
    if (!bidOptionId) {
      console.warn('Skipping bid option without ID:', option);
      return null;
    }
    const { segment_pax_count, segment_fee_per_leg, ...optionToSend } = option;
    return apiClient.updateBidOption(bidOptionId, optionToSend);
  });
  await Promise.all(promises);
};

// ─── Edit-mode helpers ───────────────────────────────────────────────────────

/**
 * Resolve the top-level `return_date` column from the form's flight data.
 *  - 1-leg round-trip shape: read flights[0].returnDate.
 *  - 2-leg round-trip shape: fall back to flights[1].departureDate.
 *  - One-way / multi-leg: null.
 * Mirrors the fallback already in `buildQuotePayload`.
 */
const resolveReturnDate = (quoteData) => {
  const zone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  const fromFirstLeg = formatDateToISOWithTimezone(
    quoteData.flights?.[0]?.returnDate,
    quoteData.flights?.[0]?.returnTime,
    zone,
  );
  if (fromFirstLeg) return fromFirstLeg;
  if (quoteData.flightType === 'round-trip') {
    return formatDateToISOWithTimezone(
      quoteData.flights?.[1]?.departureDate,
      quoteData.flights?.[1]?.departureTime,
      zone,
    ) || null;
  }
  return null;
};

/**
 * Build a PATCH payload for the underlying bid in edit mode. Mirrors
 * `buildBidUpdatePayload` but DOES NOT flip status — edit must preserve
 * whatever status the quote is currently in (DRAFT/SENT/VIEWED/SIGNED).
 */
const buildBidEditPayload = (quoteData) => {
  const finalPrice = quoteData.settings?.final_price || 0;
  return {
    client_name: getClientName(quoteData.client?.clientData),
    client_email: quoteData.client?.clientData?.email || "",
    client_phone: quoteData.client?.clientData?.phone || "",
    departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
    arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
    return_date: resolveReturnDate(quoteData),
    passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
    baggage_requirements: quoteData.flights?.[0]?.notes || "",
    special_requests: quoteData.settings?.notes || "",
    total_amount: trunc2(parseFloat(finalPrice) || 0),
    currency: "USD",
    valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
    notes: quoteData.settings?.notes || "",
    leg_itinerary: buildLegItinerary(quoteData.flights),
  };
};

/**
 * Build a PATCH payload for the quote itself. Top-level fields only,
 * deliberately excluding identifiers (quote_id, quote_number, created_by,
 * broker_org_id, bid_items) and status — status is preserved on edit.
 */
const buildQuotePatchPayload = (quoteData) => {
  const airportZone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  const settings = quoteData.settings || {};
  return {
    client_id: quoteData.client?.clientData?.client_id || quoteData.client?.clientData?.id || undefined,
    departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
    arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, airportZone) || null,
    return_date: resolveReturnDate(quoteData),
    passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
    is_round_trip: quoteData.flightType === 'round-trip',
    is_multi_leg: quoteData.flightType === 'multi-leg',
    note_to_client: settings.notes || "",
    total_due: settings.final_price || 0,
    broker_margin: settings.broker_margin || 0,
    platform_fee: settings.platform_fee || 0,
    federal_excise_tax: settings.federal_excise_tax || 0,
    segment_tax: settings.segment_tax || 0,
    operator_cost: settings.operator_cost || 0,
    taxable_amount: settings.taxable_amount || 0,
  };
};

/**
 * Build the body for a single bid_option PATCH (or POST when new).
 *
 * Explicit whitelist — the hydrated form's option includes joined records
 * (`aircraft`, `operator`) and read-only columns (`created_at`, `updated_at`,
 * `bid_option_id`) that the backend rejects with HTTP 500 if forwarded.
 * Mirrors the field set used by `createBidOptions` in the create flow.
 */
const buildBidOptionPayload = (option = {}, bidId) => ({
  bid_id: bidId,
  aircraft_id: option.aircraft_id || '',
  operator_id: option.operator_id || '',
  base_price:        Number(option.base_price        || 0).toFixed(2),
  fuel_cost:         Number(option.fuel_cost         || 0).toFixed(2),
  landing_fees:      Number(option.landing_fees      || 0).toFixed(2),
  crew_fees:         Number(option.crew_fees         || 0).toFixed(2),
  catering_cost:     Number(option.catering_cost     || 0).toFixed(2),
  ground_transport:  Number(option.ground_transport  || 0).toFixed(2),
  other_fees:        Number(option.other_fees        || 0).toFixed(2),
  total_price:       Number(option.total_price       || 0).toFixed(2),
  broker_markup:     Number(option.broker_markup     || 0).toFixed(2),
  final_price:       Number(option.final_price       || 0).toFixed(2),
  federal_excise_tax: Number(option.federal_excise_tax || 0).toFixed(2),
  segment_tax:       Number(option.segment_tax       || 0).toFixed(2),
  platform_fee:      Number(option.platform_fee      || 0).toFixed(2),
  operator_cost:     Number(option.operator_cost     || 0).toFixed(2),
  is_recommended:    !!option.is_recommended,
  recommendation_reason: option.recommendation_reason || '',
  availability_confirmed: option.availability_confirmed !== false,
  shortlisted:       option.shortlisted !== false,
  risk_level:        option.risk_level || 'LOW',
  trust_score:       Number(option.trust_score || 0).toFixed(2),
  estimated_flight_time: option.estimated_flight_time || 0,
  aircraft_features: option.aircraft_features || { beds: 1, wifi: false, entertainment: false },
});

/**
 * Reconcile bid options between the source quote and the edited form.
 *  - PATCH each option that still has its bid_option_id (existing).
 *  - POST each option without a bid_option_id (newly added in the form).
 *  - PATCH options that were removed in the form to { shortlisted: false }
 *    (soft delete; backend lacks a hard-delete endpoint for bid_options).
 */
const reconcileBidOptions = async (bidId, originalIds = [], currentOptions = []) => {
  const currentIds = new Set(currentOptions.map((o) => o.bid_option_id).filter(Boolean));
  const removedIds = originalIds.filter((id) => !currentIds.has(id));

  // 1) Existing → PATCH
  const patches = currentOptions
    .filter((o) => o.bid_option_id)
    .map((o) => apiClient.updateBidOption(o.bid_option_id, buildBidOptionPayload(o, bidId)));

  // 2) New → POST
  const creates = currentOptions
    .filter((o) => !o.bid_option_id)
    .map((o) => apiClient.createBidOption(buildBidOptionPayload(o, bidId)));

  // 3) Removed → soft-delete via PATCH shortlisted:false
  const softDeletes = removedIds.map((id) =>
    apiClient.updateBidOption(id, { shortlisted: false }),
  );

  await Promise.all([...patches, ...creates, ...softDeletes]);
};

/**
 * Hook that handles all quote processing (draft saving & sending)
 */
const useQuoteProcessor = (organization = null) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { shortlistAircraft, selectedTripGroup } = useAppStore();
  const { userProfile } = useAuthStore();

  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [error, setError] = useState(null);

  // Resume state across retries within the same mount. When a step fails after
  // earlier ones already succeeded (e.g. POST /quotes 500 after the bid +
  // bid_options were created), retrying must NOT re-create them — it resumes
  // from the failed step. Reset to all-empty on full success.
  const progressRef = useRef({ bidId: null, bidPayload: null, optionsCreated: false, createdQuote: null });

  const processQuote = useCallback(async (quoteData, { status, send }) => {
    const finalPrice = quoteData.settings.final_price || 0;
    const bidOptions = quoteData.bidOptions || [];

    const brokerOrgId = userProfile?.org_id;
    const createdBy = userProfile?.userprofile_id;

    let createdQuote;
    let createdBid;

    if (shortlistAircraft) {
      // New bid from shortlist — only create what a prior attempt didn't.
      if (!progressRef.current.bidId) {
        const bidPayload = buildBidPayload(quoteData, { finalPrice, createdBy, brokerOrgId });
        const bid = await apiClient.createBid(bidPayload);
        progressRef.current.bidId = bid.bid_id;
        progressRef.current.bidPayload = bidPayload;
      }
      const bidId = progressRef.current.bidId;

      if (!progressRef.current.optionsCreated) {
        await createBidOptions(bidId, bidOptions);
        progressRef.current.optionsCreated = true;
      }
      createdBid = { ...(progressRef.current.bidPayload || {}), bid_id: bidId };

      if (!progressRef.current.createdQuote) {
        const quoteArgs = {
          brokerOrgId,
          createdBy,
          bidItems: [{ bid_id: bidId }],
          status,
          settings: quoteData.settings
        };
        progressRef.current.createdQuote = send
          ? await sendQuoteToClient(buildQuotePayload(quoteData, quoteArgs), queryClient, organization, userProfile)
          : await apiClient.createQuote(buildQuoteCreatePayload(quoteData, quoteArgs));
      }
      createdQuote = progressRef.current.createdQuote;

      await apiClient.updateBid(bidId, { status: 'SENT' });

    } else {
      // Existing trip group bid. updateExistingBidOptions PATCHes by id (safe to
      // repeat); only the quote create is guarded against duplication on retry.
      await updateExistingBidOptions(bidOptions);

      if (!progressRef.current.createdQuote) {
        const quoteArgs = {
          brokerOrgId: userProfile?.org_id || brokerOrgId,
          createdBy,
          bidItems: selectedTripGroup?.bids?.map(bid => ({ bid_id: bid.bid_id })) || [],
          status,
          settings: quoteData.settings
        };
        progressRef.current.createdQuote = send
          ? await sendQuoteToClient(buildQuotePayload(quoteData, quoteArgs), queryClient, organization, userProfile)
          : await apiClient.createQuote(buildQuoteCreatePayload(quoteData, quoteArgs));
      }
      createdQuote = progressRef.current.createdQuote;

      const selectedBidId = selectedTripGroup?.bids?.[0]?.bid_id;
      if (selectedBidId) {
        await apiClient.updateBid(selectedBidId, buildBidUpdatePayload(quoteData, finalPrice));
      }
    }

    // Upload contract file and save to org branding history if admin
    const contractFile = quoteData.settings?.contractFile?.file;
    let contractPath = quoteData.settings?.contractFile?.path;
    if (contractFile) {
      const quoteId = createdQuote?.quote_id || `Q${Date.now()}`;
      //const renamed = new File([contractFile], `quote_${quoteId}_${contractFile.name}`, { type: contractFile.type });
      //const uploaded = await apiClient.uploadFile(renamed, "contract");

      // file info
      const file = contractFile;
      const key = `contracts/quotes/${quoteId}_${file.name}`;

      // get s3 signed urls
      const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
      
      // upload file to s3 and return public url
      contractPath = await apiClient.uploadFileToS3(file, s3Urls);

      // Save contract path to quote record
      const quoteRecordId = createdQuote?.quote_id;
      if (quoteRecordId) {
        await apiClient.updateQuote(quoteRecordId, { files: { contract: contractPath } });
      }
    }
    if (contractPath && ['admin', 'ADMIN'].includes(userProfile?.role) && organization) {
      const existingHistory = organization?.branding?.contractHistory || [];
      await apiClient.updateOrganization(brokerOrgId, {
        branding: {
          ...organization?.branding,
          contractHistory: [...new Set([contractPath, ...existingHistory])],
        },
      });
    }

    // Optimistically seed the quotes cache so QuotesList renders the new quote
    // instantly when the user lands on /quotes (its background refetch will
    // reconcile shortly after).
    const optimisticBidItems = shortlistAircraft
      ? [{
          bid: {
            ...createdBid,
            bid_options: bidOptions.map(opt => ({ ...opt, shortlisted: true })),
          },
        }]
      : (selectedTripGroup?.bids || []).map(bid => ({
          bid: {
            ...bid,
            bid_options: (bid.bid_options || []).map(opt => ({ ...opt, shortlisted: true })),
          },
        }));

    const optimisticQuote = {
      ...createdQuote,
      id: createdQuote?.quote_id || createdQuote?.id,
      status: createdQuote?.status || status,
      client: createdQuote?.client || quoteData.client?.clientData || {},
      bid_items: optimisticBidItems,
    };

    queryClient.setQueriesData(
      { predicate: (q) => q.queryKey[0] === 'quotes' && q.queryKey[1] === 'all' },
      (old) => [optimisticQuote, ...(old || [])]
    );

    // Fire-and-forget: refresh quotes and shortlistedBids in the background so
    // server-truth reconciles without blocking navigation.
    queryClient.invalidateQueries({ queryKey: ['quotes'] });

    // Everything succeeded — clear resume state so the next quote starts fresh.
    progressRef.current = { bidId: null, bidPayload: null, optionsCreated: false, createdQuote: null };

    toast.success(send ? 'Quote sent to client' : 'Draft saved');
    setTimeout(() => navigate(ROUTES.QUOTES.path), 500);
  }, [shortlistAircraft, selectedTripGroup, userProfile, queryClient, navigate, organization]);

  const handleSaveDraft = useCallback(async (quoteData) => {
    try {
      setIsSavingDraft(true);
      setError(null);
      await processQuote(quoteData, { status: 'DRAFT', send: false });
    } catch (err) {
      setError(err.message || 'Failed to save draft. Please try again.');
      setIsSavingDraft(false);
    }
  }, [processQuote]);

  const handleSendQuote = useCallback(async (quoteData) => {
    try {
      setIsSending(true);
      setError(null);
      await processQuote(quoteData, { status: 'SENT', send: true });
    } catch (err) {
      console.error('sendQuote error:', err);
      setError(err.message || 'Failed to process quote. Please try again.');
      setIsSending(false);
    }
  }, [processQuote]);

  /**
   * Edit-mode submit. Fires on Save Changes in `GenerateQuote` when the
   * form was hydrated via `?quoteid=`. Sequence:
   *   1. PATCH /bids/:bid_id   (legs, dates, valid_until, etc.)
   *   2. Reconcile bid_options (PATCH existing, POST new, soft-delete removed)
   *   3. PATCH /quotes/:quote_id (top-level fields; status untouched)
   *   4. Invalidate caches so the list / drawer / tax report pick up changes.
   * Status is intentionally preserved.
   */
  const handleUpdateQuote = useCallback(async (quoteData) => {
    const editing = quoteData?._editing;
    if (!editing?.quote_id) {
      setError('Edit mode requires an existing quote_id');
      return;
    }
    try {
      setIsSending(true);
      setError(null);

      // 1) PATCH the bid (legs / dates / valid_until / passenger_count).
      if (editing.bid_id) {
        await apiClient.updateBid(editing.bid_id, buildBidEditPayload(quoteData));
      }

      // 2) Reconcile bid options.
      if (editing.bid_id) {
        await reconcileBidOptions(
          editing.bid_id,
          editing.bid_option_ids || [],
          quoteData.bidOptions || [],
        );
      }

      // 3) PATCH the quote itself (top-level fields, no status change).
      await apiClient.updateQuote(editing.quote_id, buildQuotePatchPayload(quoteData));

      // 4) Cache invalidation.
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', editing.quote_id] });
      queryClient.invalidateQueries({ queryKey: ['tax-reports'] });

      toast.success('Quote updated');
      setTimeout(() => navigate(ROUTES.QUOTES.path), 500);
    } catch (err) {
      console.error('updateQuote error:', err);
      setError(err.message || 'Failed to save changes. Please try again.');
      toast.error('Failed to save changes');
    } finally {
      setIsSending(false);
    }
  }, [queryClient, navigate]);

  /**
   * Edit-mode submit + email notify. Same PATCH chain as `handleUpdateQuote`
   * (preserves status), then sends an updated proposal email to the client.
   */
  const handleUpdateQuoteAndNotify = useCallback(async (quoteData) => {
    const editing = quoteData?._editing;
    if (!editing?.quote_id) {
      setError('Edit mode requires an existing quote_id');
      return;
    }
    try {
      setIsSending(true);
      setError(null);

      // 1) PATCH the bid. Flip bid status to SENT when transitioning a
      // DRAFT quote on first send, matching the create flow's behaviour.
      if (editing.bid_id) {
        const bidPatch = buildBidEditPayload(quoteData);
        if (editing.source_status === 'DRAFT') {
          bidPatch.status = 'SENT';
        }
        await apiClient.updateBid(editing.bid_id, bidPatch);
      }

      // 2) Reconcile bid options.
      if (editing.bid_id) {
        await reconcileBidOptions(
          editing.bid_id,
          editing.bid_option_ids || [],
          quoteData.bidOptions || [],
        );
      }

      // 3) PATCH the quote itself. If the source was DRAFT and we're emailing
      // the client for the first time, flip the status to SENT to match the
      // create flow. Statuses past DRAFT (SENT/VIEWED/SIGNED) are preserved.
      const quotePatch = buildQuotePatchPayload(quoteData);
      if (editing.source_status === 'DRAFT') {
        quotePatch.status = 'SENT';
      }
      await apiClient.updateQuote(editing.quote_id, quotePatch);

      // 4) Send notification email referencing the EXISTING quote_id.
      await notifyClientOfQuoteUpdate({
        quoteId: editing.quote_id,
        quoteData,
        organization,
        userProfile,
      });

      // 5) Cache invalidation.
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', editing.quote_id] });
      queryClient.invalidateQueries({ queryKey: ['tax-reports'] });

      toast.success('Quote updated and email sent');
      setTimeout(() => navigate(ROUTES.QUOTES.path), 500);
    } catch (err) {
      console.error('updateQuoteAndNotify error:', err);
      setError(err.message || 'Failed to save changes. Please try again.');
      toast.error('Failed to save changes');
    } finally {
      setIsSending(false);
    }
  }, [queryClient, navigate, organization]);

  return {
    handleSaveDraft,
    handleSendQuote,
    handleUpdateQuote,
    handleUpdateQuoteAndNotify,
    isSending,
    isSavingDraft,
    error,
    setError,
  };
};

export default useQuoteProcessor;
